import { useMemo } from "react";
import StatusBadge from "@/components/StatusBadge";
import { shotsApi } from "@/lib/api";
import { toast } from "sonner";

export default function ShotCalendar({ shots, setShots, onReload }) {
  const groups = useMemo(() => {
    const map = {};
    shots.forEach((s) => {
      const d = s.scheduled_date || "unscheduled";
      if (!map[d]) map[d] = [];
      map[d].push(s);
    });
    const keys = Object.keys(map).sort((a, b) => {
      if (a === "unscheduled") return 1;
      if (b === "unscheduled") return -1;
      return a.localeCompare(b);
    });
    return keys.map((k) => ({ date: k, items: map[k] }));
  }, [shots]);

  const handleStatusChange = async (id, status) => {
    try {
      const updated = await shotsApi.update(id, { status });
      setShots((s) => s.map((x) => (x.id === id ? updated : x)));
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleSchedule = async (id, date) => {
    try {
      const updated = await shotsApi.update(id, { scheduled_date: date });
      setShots((s) => s.map((x) => (x.id === id ? updated : x)));
      toast.success("Schedule updated");
    } catch {
      toast.error("Failed to schedule");
    }
  };

  if (shots.length === 0) {
    return (
      <div className="border border-dashed border-zinc-800 rounded-md p-12 text-center text-zinc-500 text-sm">
        No shots to schedule yet.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {groups.map((g) => (
        <div key={g.date} data-testid={`schedule-day-${g.date}`}>
          <div className="flex items-baseline gap-3 mb-3">
            <div className="font-heading text-xl text-zinc-100">
              {g.date === "unscheduled" ? "Unscheduled" : formatDate(g.date)}
            </div>
            <div className="text-xs text-zinc-500 font-mono">
              {g.items.length} shots
            </div>
          </div>
          <div className="border border-zinc-800 rounded-md overflow-hidden bg-zinc-900">
            {g.items.map((s, i) => (
              <div
                key={s.id}
                className={`flex items-center gap-4 px-4 py-3 ${
                  i < g.items.length - 1 ? "border-b border-zinc-800/60" : ""
                } hover:bg-zinc-800/30 transition-colors`}
              >
                <div className="w-20 text-overline text-zinc-500 shrink-0">
                  S{s.scene || "—"} · {s.shot_number || "—"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-200 line-clamp-1">
                    {s.description || "Untitled shot"}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5 truncate">
                    {[s.shot_size, s.lens, s.location].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <input
                  type="date"
                  data-testid={`schedule-input-${s.id}`}
                  value={s.scheduled_date || ""}
                  onChange={(e) => handleSchedule(s.id, e.target.value)}
                  className="bg-zinc-950 border border-zinc-800 text-zinc-100 rounded-sm px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-amber-500"
                />
                <StatusBadge
                  value={s.status}
                  onChange={(v) => handleStatusChange(s.id, v)}
                  testId={`cal-status-${s.id}`}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function formatDate(d) {
  try {
    const [y, m, day] = d.split("-").map(Number);
    const dt = new Date(y, (m || 1) - 1, day || 1);
    return dt.toLocaleDateString(undefined, {
      weekday: "long",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return d;
  }
}
